import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { isIOS } from "react-device-detect";
import ImageSlider from "../../components/ImageSlider";
import { useGPTStore } from "../../store/useGPT";
import FortuneLabPrivacyPolicyModal from "../../components/FortuneLabPrivacyPolicyModal";
import ThirdPartyConsentModal from "../../components/ThirdPartyConsentModal";
import BusinessInfoFooter from "../../components/BusinessInfoFooter";
import { usePixel, PixelInfo } from "../../hooks/usePixel";

interface FormState {
  name: string;
  phoneNumber: string;
  birthDate: string;
  birthTime: string;
  solarType: "SOLAR" | "LUNAR" | "LUNAR_INTER";
  sex: "M" | "F";
}

interface FormErrors {
  name?: string;
  phoneNumber?: string;
  birthDate?: string;
  birthTime?: string;
  agreement?: string;
}

const MAX_NAME_LENGTH = 20;
const MAX_PHONE_LENGTH = 11;

const sanitizePhoneNumber = (value: string) => value.replace(/\D/g, "").slice(0, MAX_PHONE_LENGTH);

const formatBirthDate = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}.${digits.slice(4)}`;
  return `${digits.slice(0, 4)}.${digits.slice(4, 6)}.${digits.slice(6)}`;
};

const formatBirthTime = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
};

const isValidBirthDate = (value: string) => {
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 8) return false;

  const year = parseInt(digits.slice(0, 4), 10);
  const month = parseInt(digits.slice(4, 6), 10);
  const day = parseInt(digits.slice(6, 8), 10);

  const currentYear = new Date().getFullYear();
  if (year < 1900 || year > currentYear) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  const daysInMonth = new Date(year, month, 0).getDate();
  if (day > daysInMonth) return false;

  return true;
};

const isValidBirthTime = (value: string) => {
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 4) return false;

  const hour = parseInt(digits.slice(0, 2), 10);
  const minute = parseInt(digits.slice(2, 4), 10);

  if (hour < 0 || hour > 23) return false;
  if (minute < 0 || minute > 59) return false;

  return true;
};

const FORTUNE_LAB_BASE_URL =
  process.env.REACT_APP_FORTUNE_LAB_BASE_URL || process.env.REACT_APP_BASE_URL || "";

const isValidKoreanPhoneNumber = (value: string) => {
  if (!/^01[016789]\d{7,8}$/.test(value)) {
    return false;
  }

  const subscriber = value.slice(3);
  if (/^(\d)\1+$/.test(subscriber)) {
    return false;
  }

  if (isSequentialDigits(subscriber)) {
    return false;
  }

  return true;
};

const isSequentialDigits = (value: string) => {
  if (value.length <= 1) {
    return false;
  }

  let isAscending = true;
  let isDescending = true;

  for (let i = 1; i < value.length; i += 1) {
    const current = Number(value[i]);
    const previous = Number(value[i - 1]);

    if (current - previous !== 1) {
      isAscending = false;
    }

    if (current - previous !== -1) {
      isDescending = false;
    }

    if (!isAscending && !isDescending) {
      return false;
    }
  }

  return isAscending || isDescending;
};

const ConsultMainPage = () => {
  const navigate = useNavigate();
  const { trkId: trkIdFromPath } = useParams<{ trkId?: string }>();
  const [searchParams] = useSearchParams();
  const trkIdFromQuery = searchParams.get("trkId") || "";
  const resolvedTrkId = (trkIdFromPath ?? trkIdFromQuery).trim();
  const { trackEvent } = useGPTStore();

  const [companyName, setCompanyName] = useState<string | null>(null);
  const [contactName, setContactName] = useState<string | null>(null);
  const [contactTel, setContactTel] = useState<string | null>(null);
  const [contactEmail, setContactEmail] = useState<string | null>(null);
  const [companyNumber, setCompanyNumber] = useState<string | null>(null);
  const [mailOrderNumber, setMailOrderNumber] = useState<string | null>(null);
  const [bizProfileName, setBizProfileName] = useState<string | null>(null);
  const [showFooter, setShowFooter] = useState(true);
  const [topImageUrls, setTopImageUrls] = useState<string[]>([]);
  const [bottomImageUrls, setBottomImageUrls] = useState<string[]>([]);
  const [consultButtonName, setConsultButtonName] = useState<string>("");
  const [documentTitle, setDocumentTitle] = useState<string>("");
  const [dataLoading, setDataLoading] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [pixelInfo, setPixelInfo] = useState<PixelInfo | null>(null);
  const [isPrivacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [isThirdPartyModalOpen, setThirdPartyModalOpen] = useState(false);

  // 픽셀 훅 사용
  const { trackConversion } = usePixel(pixelInfo);
  const [formState, setFormState] = useState<FormState>({
    name: "",
    phoneNumber: "",
    birthDate: "",
    birthTime: "",
    solarType: "SOLAR",
    sex: "M",
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isPrivacyAgreementChecked, setPrivacyAgreementChecked] = useState(false);
  const [isThirdPartyAgreementChecked, setThirdPartyAgreementChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    document.title = documentTitle || "상담 신청";
  }, [documentTitle]);

  useEffect(() => {
    if (!resolvedTrkId) {
      localStorage.removeItem("trkId");
      return;
    }

    const landingKey = `consultOrderLanding:${resolvedTrkId}`;
    if (!localStorage.getItem(landingKey)) {
      fetch(`${FORTUNE_LAB_BASE_URL}/consult-order/tracking/landing`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ trkId: resolvedTrkId }),
      }).catch((error) => console.error("consult landing track error", error));
      localStorage.setItem(landingKey, "true");
    }

    localStorage.setItem("trkId", resolvedTrkId);
    trackEvent(resolvedTrkId);
  }, [resolvedTrkId, trackEvent]);

  useEffect(() => {
    if (!resolvedTrkId) {
      setCompanyName(null);
      setContactName(null);
      setContactTel(null);
      setContactEmail(null);
      setTopImageUrls([]);
      setBottomImageUrls([]);
      setShowFooter(true);
      setMailOrderNumber(null);
      setDataLoading(false);
      return;
    }

    if (!FORTUNE_LAB_BASE_URL) {
      setDataLoading(false);
      return;
    }

    let isCancelled = false;

    const fetchTrackingInfo = async () => {
      try {
        const response = await fetch(
          `${FORTUNE_LAB_BASE_URL}/consult-order/tracking/info?trkId=${resolvedTrkId}`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (isCancelled) {
          return;
        }

        const info = data?.contents ?? {};

        const fetchedCompanyName = info?.companyName;
        const fetchedContactName = info?.ceoName;
        const fetchedContactTel = info?.contactTel;
        const fetchedContactEmail = info?.contactEmail;
        const fetchedTopImages = Array.isArray(info?.topImageUrls)
          ? info.topImageUrls.filter((item: unknown): item is string => typeof item === 'string' && item.trim().length > 0)
          : [];
        const fetchedBottomImages = Array.isArray(info?.bottomImageUrls)
          ? info.bottomImageUrls.filter((item: unknown): item is string => typeof item === 'string' && item.trim().length > 0)
          : [];

        setCompanyName(
          typeof fetchedCompanyName === "string" && fetchedCompanyName.trim().length > 0
            ? fetchedCompanyName.trim()
            : null
        );
        setContactName(
          typeof fetchedContactName === "string" && fetchedContactName.trim().length > 0
            ? fetchedContactName.trim()
            : null
        );
        setContactTel(
          typeof fetchedContactTel === "string" && fetchedContactTel.trim().length > 0
            ? fetchedContactTel.trim()
            : null
        );
        setContactEmail(
          typeof fetchedContactEmail === "string" && fetchedContactEmail.trim().length > 0
            ? fetchedContactEmail.trim()
            : null
        );
        setTopImageUrls(fetchedTopImages.map((url: string) => url.trim()));
        setBottomImageUrls(fetchedBottomImages.map((url: string) => url.trim()));
        setConsultButtonName(info?.consultButtonName ?? "");
        setDocumentTitle(info?.documentTitle ?? "");
        setShowFooter(info?.showFooter !== false);

        // 픽셀 정보 설정
        setPixelInfo({
          facebookPixelId: info?.facebookPixelId ?? null,
          kakaoPixelId: info?.kakaoPixelId ?? null,
          googleAdsPixelId: info?.googleAdsPixelId ?? null,
          googleAdsEventSnippet: info?.googleAdsEventSnippet ?? null,
          daangnPixelId: info?.daangnPixelId ?? null,
          tiktokPixelId: info?.tiktokPixelId ?? null,
        });

        // 사업자 정보 설정
        setCompanyNumber(
          typeof info?.companyNumber === "string" && info.companyNumber.trim().length > 0
            ? info.companyNumber.trim()
            : null
        );
        setMailOrderNumber(
          typeof info?.mailOrderNumber === "string" && info.mailOrderNumber.trim().length > 0
            ? info.mailOrderNumber.trim()
            : null
        );
        setBizProfileName(
          typeof info?.bizProfileName === "string" && info.bizProfileName.trim().length > 0
            ? info.bizProfileName.trim()
            : null
        );
      } catch (fetchError) {
        console.error("consult-order tracking info error", fetchError);
        if (!isCancelled) {
          setCompanyName(null);
          setContactName(null);
          setContactTel(null);
          setContactEmail(null);
          setCompanyNumber(null);
          setMailOrderNumber(null);
          setBizProfileName(null);
          setTopImageUrls([]);
          setBottomImageUrls([]);
          setShowFooter(true);
          setPixelInfo(null);
        }
      } finally {
        if (!isCancelled) {
          setDataLoading(false);
        }
      }
    };

    fetchTrackingInfo();

    return () => {
      isCancelled = true;
    };
  }, [resolvedTrkId]);

  // 이미지 프리로드 (API 로딩 완료 후)
  useEffect(() => {
    if (dataLoading) return;

    const allImageUrls = [...topImageUrls, ...bottomImageUrls];
    if (allImageUrls.length === 0) {
      setImagesLoaded(true);
      return;
    }

    let loadedCount = 0;
    allImageUrls.forEach((url) => {
      const img = new Image();
      img.onload = img.onerror = () => {
        loadedCount++;
        if (loadedCount === allImageUrls.length) {
          setImagesLoaded(true);
        }
      };
      img.src = url;
    });
  }, [topImageUrls, bottomImageUrls, dataLoading]);

  useEffect(() => {
    const updateMainStyle = () => {
      const mainElement = document.querySelector(".main_container main");
      if (mainElement instanceof HTMLElement) {
        const isKakaoOS = navigator.userAgent.toLowerCase().includes("kakao");
        let additionalPadding = 0;

        if (isIOS && !isKakaoOS) {
          additionalPadding = 100;
        } else if (isIOS && isKakaoOS) {
          additionalPadding = 40;
        }

        mainElement.style.paddingBottom = `${additionalPadding}px`;
      }
    };

    updateMainStyle();
    window.addEventListener("resize", updateMainStyle);
    return () => window.removeEventListener("resize", updateMainStyle);
  }, []);

  const handleChange = (field: keyof FormState) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value;

      let nextValue: string;
      if (field === "name") {
        nextValue = rawValue.slice(0, MAX_NAME_LENGTH);
      } else if (field === "phoneNumber") {
        nextValue = sanitizePhoneNumber(rawValue);
      } else if (field === "birthDate") {
        nextValue = formatBirthDate(rawValue);
      } else if (field === "birthTime") {
        nextValue = formatBirthTime(rawValue);
      } else {
        nextValue = rawValue;
      }

      setFormState((prev) => ({ ...prev, [field]: nextValue }));

      // 실시간 검증
      if (field === "birthDate") {
        const digits = nextValue.replace(/\D/g, "");
        if (digits.length === 8 && !isValidBirthDate(nextValue)) {
          setFormErrors((prev) => ({ ...prev, birthDate: "유효한 생년월일을 입력해주세요." }));
        } else {
          setFormErrors((prev) => ({ ...prev, birthDate: undefined }));
        }
      } else if (field === "birthTime") {
        const digits = nextValue.replace(/\D/g, "");
        if (digits.length === 4 && !isValidBirthTime(nextValue)) {
          setFormErrors((prev) => ({ ...prev, birthTime: "유효한 시간을 입력해주세요. (00:00 ~ 23:59)" }));
        } else {
          setFormErrors((prev) => ({ ...prev, birthTime: undefined }));
        }
      } else {
        setFormErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  const validate = () => {
    const nextErrors: FormErrors = {};

    const trimmedName = formState.name.trim();
    if (!trimmedName) {
      nextErrors.name = "이름을 입력해주세요.";
    } else if (trimmedName.length > MAX_NAME_LENGTH) {
      nextErrors.name = "이름은 20자 이하로 입력해주세요.";
    }

    const phone = sanitizePhoneNumber(formState.phoneNumber);
    if (!phone) {
      nextErrors.phoneNumber = "전화번호를 입력해주세요.";
    } else if (!isValidKoreanPhoneNumber(phone)) {
      nextErrors.phoneNumber = "유효한 휴대전화번호를 입력해주세요.";
    }

    // if (formState.birthDate && !isValidBirthDate(formState.birthDate)) {
    //   nextErrors.birthDate = "유효한 생년월일을 입력해주세요.";
    // }
    //
    // if (formState.birthTime && !isValidBirthTime(formState.birthTime)) {
    //   nextErrors.birthTime = "유효한 시간을 입력해주세요. (00:00 ~ 23:59)";
    // }

    if (!isPrivacyAgreementChecked || !isThirdPartyAgreementChecked) {
      nextErrors.agreement = "개인정보 수집·이용 및 제3자 제공에 동의해주세요.";
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    const trkId = localStorage.getItem("trkId") || "";
    const sanitizedPhoneNumber = sanitizePhoneNumber(formState.phoneNumber);

    // 알림톡 발송
    if (trkId) {
      try {
        await fetch(`${FORTUNE_LAB_BASE_URL}/consult-order/tracking/send-alimtalk`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            trkId,
            phone: sanitizedPhoneNumber,
            name: formState.name.trim(),
            birthDate: formState.birthDate.trim() || undefined,
            birthTime: formState.birthTime.trim() || undefined,
            solarType: formState.solarType,
            sex: formState.sex,
          }),
        });
      } catch (alimtalkError) {
        console.error("알림톡 발송 실패", alimtalkError);
      }
    }

    // 픽셀 전환 이벤트 발동
    trackConversion();

    navigate("/consult/result");
  };

  if (dataLoading || !imagesLoaded) {
    return (
      <div className="main_container">
        <main style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
          <p>로딩 중...</p>
        </main>
      </div>
    );
  }

  return (
    <>
      <div className="main_container">
        <main className="landing">
          {topImageUrls.length > 0 && (
            <div
              style={{
                width: '100%',
                marginBottom: '24px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {topImageUrls.map((url, index) => (
                <img
                  key={`top-${url}-${index}`}
                  src={url}
                  alt={`상단 이미지 ${index + 1}`}
                  style={{ width: '100%' }}
                />
              ))}
            </div>
          )}

          {topImageUrls.length === 0 && bottomImageUrls.length === 0 && (
            <>
              <ImageSlider />

              <div className="title highlight">이벤트 참여 방법</div>
              <br />
              <div className="title" style={{ fontSize: "20px" }}>1. 아래 신청서 양식을 작성해주세요.</div>
              <br />
              <div className="title" style={{ fontSize: "20px" }}>2. 상담신청 완료 카톡을 보내드립니다.</div>
              <br />
              <div className="title" style={{ fontSize: "20px" }}>3. 카카오톡으로 고민과 생년월일을 남겨주세요
                <br />(문의량이 많을 경우 답변이 늦어질 수 있습니다)
              </div>
              <br />
              <div className="title highlight">질문 가능 영역</div>
              <br />
              <div className="title" style={{ fontSize: "30px" }}>연애/결혼/재회/재물/사업/신년</div>
              <br />
              <div className="title highlight-red" style={{ fontSize: "30px" }}>비용: 1만원(추가비용 일절 없음)</div>
            </>
          )}

          <form className="fortune_lab_form" onSubmit={handleSubmit}>
            <input
              type="text"
              placeholder="이름"
              value={formState.name}
              onChange={handleChange("name")}
              autoComplete="name"
              maxLength={MAX_NAME_LENGTH}
            />
            {formErrors.name && (
              <div className="input_error">{formErrors.name}</div>
            )}

            <input
              type="tel"
              placeholder="전화번호 (숫자만 입력)"
              value={formState.phoneNumber}
              onChange={handleChange("phoneNumber")}
              autoComplete="tel"
              inputMode="numeric"
              maxLength={MAX_PHONE_LENGTH}
            />
            {formErrors.phoneNumber && (
              <div className="input_error">{formErrors.phoneNumber}</div>
            )}

            {/*<input*/}
            {/*  type="text"*/}
            {/*  placeholder="생년월일 (yyyy.mm.dd)"*/}
            {/*  value={formState.birthDate}*/}
            {/*  onChange={handleChange("birthDate")}*/}
            {/*  autoComplete="bday"*/}
            {/*  inputMode="numeric"*/}
            {/*/>*/}
            {/*{formErrors.birthDate && (*/}
            {/*  <div className="input_error">{formErrors.birthDate}</div>*/}
            {/*)}*/}

            {/*<input*/}
            {/*  type="text"*/}
            {/*  placeholder="생시 (예: 14:30, 선택사항)"*/}
            {/*  value={formState.birthTime}*/}
            {/*  onChange={handleChange("birthTime")}*/}
            {/*  inputMode="numeric"*/}
            {/*/>*/}
            {/*{formErrors.birthTime && (*/}
            {/*  <div className="input_error">{formErrors.birthTime}</div>*/}
            {/*)}*/}

            {/*<select*/}
            {/*  value={formState.solarType}*/}
            {/*  onChange={(e) =>*/}
            {/*    setFormState((prev) => ({*/}
            {/*      ...prev,*/}
            {/*      solarType: e.target.value as FormState["solarType"],*/}
            {/*    }))*/}
            {/*  }*/}
            {/*  style={{*/}
            {/*    width: "84%",*/}
            {/*    height: "52px",*/}
            {/*    border: "1px solid #dddddd",*/}
            {/*    borderRadius: "8px",*/}
            {/*    padding: "0 40px 0 16px",*/}
            {/*    fontSize: "16px",*/}
            {/*    outline: "none",*/}
            {/*    backgroundColor: "#fff",*/}
            {/*    appearance: "none",*/}
            {/*    WebkitAppearance: "none",*/}
            {/*    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,*/}
            {/*    backgroundRepeat: "no-repeat",*/}
            {/*    backgroundPosition: "right 16px center",*/}
            {/*  }}*/}
            {/*>*/}
            {/*  <option value="SOLAR">양력</option>*/}
            {/*  <option value="LUNAR">음력</option>*/}
            {/*  <option value="LUNAR_INTER">음력 (윤달)</option>*/}
            {/*</select>*/}

            {/*<select*/}
            {/*  value={formState.sex}*/}
            {/*  onChange={(e) =>*/}
            {/*    setFormState((prev) => ({*/}
            {/*      ...prev,*/}
            {/*      sex: e.target.value as FormState["sex"],*/}
            {/*    }))*/}
            {/*  }*/}
            {/*  style={{*/}
            {/*    width: "84%",*/}
            {/*    height: "52px",*/}
            {/*    border: "1px solid #dddddd",*/}
            {/*    borderRadius: "8px",*/}
            {/*    padding: "0 40px 0 16px",*/}
            {/*    fontSize: "16px",*/}
            {/*    outline: "none",*/}
            {/*    backgroundColor: "#fff",*/}
            {/*    appearance: "none",*/}
            {/*    WebkitAppearance: "none",*/}
            {/*    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,*/}
            {/*    backgroundRepeat: "no-repeat",*/}
            {/*    backgroundPosition: "right 16px center",*/}
            {/*  }}*/}
            {/*>*/}
            {/*  <option value="M">남성</option>*/}
            {/*  <option value="F">여성</option>*/}
            {/*</select>*/}

            <div className="privacy_agreement">
          <label className="privacy_agreement_checkbox">
            <input
              type="checkbox"
              checked={isPrivacyAgreementChecked}
              onChange={(event) => setPrivacyAgreementChecked(event.target.checked)}
            />
            <span className="privacy_agreement_text">
              개인정보 수집·이용에 동의합니다
              <button
                type="button"
                className="privacy_link_button privacy_agreement_link"
                onClick={() => setPrivacyModalOpen(true)}
              >
                개인정보 처리방침
              </button>
            </span>
          </label>
          <label className="privacy_agreement_checkbox">
            <input
                type="checkbox"
                checked={isThirdPartyAgreementChecked}
                onChange={(event) => setThirdPartyAgreementChecked(event.target.checked)}
            />
            <span className="privacy_agreement_text">
              제3자 제공에 동의합니다
              <button
                  type="button"
                  className="privacy_link_button privacy_agreement_link"
                  onClick={() => setThirdPartyModalOpen(true)}
              >
                자세히 보기
              </button>
            </span>
          </label>
        </div>
        {formErrors.agreement && (
          <div className="input_error">{formErrors.agreement}</div>
        )}

        <button type="submit" className="kakao_button" disabled={isSubmitting} style={{marginBottom: "24px"}}>
          {isSubmitting ? "진행 중..." : (consultButtonName || "카카오톡 상담 신청하기")}
        </button>
      </form>

          {bottomImageUrls.length > 0 && (
            <div
              style={{
                width: '100%',
                marginTop: '24px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {bottomImageUrls.map((url, index) => (
                <img
                  key={`bottom-${url}-${index}`}
                  src={url}
                  alt={`하단 이미지 ${index + 1}`}
                  style={{ width: '100%' }}
                />
              ))}
            </div>
          )}

          {showFooter && (
            <BusinessInfoFooter
              companyNumber={companyNumber}
              mailOrderNumber={mailOrderNumber}
              ceoName={contactName}
              bizProfileName={bizProfileName}
              companyName={companyName}
            />
          )}
        </main>
      </div>
      <FortuneLabPrivacyPolicyModal
        open={isPrivacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        companyName={companyName}
        contactName={contactName}
        contactTel={contactTel}
        contactEmail={contactEmail}
        useDefaultFallback={resolvedTrkId.length === 0}
      />
      <ThirdPartyConsentModal
        open={isThirdPartyModalOpen}
        onClose={() => setThirdPartyModalOpen(false)}
      />
    </>
  );
};

export default ConsultMainPage;
